import React, { useState, useCallback } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Popover } from "@mui/material";
import _ from "lodash";

import { PresentationFilterType } from "js/controllers/PresentationLibraryController";
import { WorkspaceResourceFolderType } from "common/interfaces";
import { FolderPathInfo, getFolderPathsForPresentation } from "js/editor/PresentationLibrary/utils/folderPaths";
import { IWorkspaceResource } from "common/interfaces";
import { IWorkspaceResourceFolder } from "common/interfaces";
import { Icon } from "js/Components/Icon";
import { LibraryBreadcrumb } from "js/editor/PresentationLibrary/components/LibraryBreadcrumb";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { Divider } from "js/Components/Divider";

const FOLDER_TOOLTIP_CLASS = "folder-location-tooltip";

const FolderTooltipStyles = createGlobalStyle`
    .${FOLDER_TOOLTIP_CLASS}.MuiTooltip-tooltip {
        padding: 8px 12px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
        max-width: min(600px, 90vw);
        width: max-content;
    }
`;

const FolderTrigger = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    text-transform: capitalize;
    user-select: none;
    min-width: 0;

    &:hover .folder-path-text {
        text-decoration: underline;
    }
`;

const PathLabelWrapper = styled.span`
    min-width: 0;
    flex: 1;
    display: block;
    overflow: hidden;
`;

const NoFolderPlaceholder = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #999;
    cursor: default;
`;

const SinglePathLabel = styled.span`
    display: block;
    width: 100%;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PopoverPaper = styled.div`
    padding: 8px;
    display: flex;
    flex-direction: column;
    min-width: 180px;
    max-width: 250px;
    font-size: 14px;
    gap: 10px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
    justify-content: center;
    align-items: flex-start;
`;

const FolderPathItem = styled.div`
    text-transform: capitalize;
    display: flex;
    align-items: center;
    gap: 4px;
    color: #333;
    cursor: pointer;
    width: 100%;
    min-width: 0;

    &:hover {
        background: #f5f5f5;
    }

    &:hover .folder-path-item-label {
        text-decoration: underline;
    }
`;

const FolderPathItemLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    min-width: 0;
`;

export interface FolderLocationBreadcrumbProps {
    presentationId: string;
    resources: IWorkspaceResource[];
    resourceFolders: IWorkspaceResourceFolder[];
    onNavigateToFolder: (filter: {
        type: PresentationFilterType.FOLDER | PresentationFilterType.TEAM_FOLDER;
        folderId: string;
        subFolderId?: string;
    }) => void;
    isGrid?: boolean;
    showDivider?: boolean;
}

export function FolderLocationBreadcrumb({
    showDivider = false,
    presentationId,
    resources,
    resourceFolders,
    onNavigateToFolder,
    isGrid = false,
}: FolderLocationBreadcrumbProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const folderPaths = getFolderPathsForPresentation(presentationId, resources, resourceFolders);

    const handlePopoverClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const navigateToFolder = useCallback(({ folderType, parentFolderId, folderId }: FolderPathInfo) => {
        const isTeamFolder =
                folderType === WorkspaceResourceFolderType.SHARED ||
                folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;
        onNavigateToFolder({
            type: isTeamFolder ? PresentationFilterType.TEAM_FOLDER : PresentationFilterType.FOLDER,
            folderId: parentFolderId ?? folderId,
            subFolderId: parentFolderId ? folderId : undefined,
        });
    }, [onNavigateToFolder]);

    const handlePathClick = useCallback((event: React.MouseEvent, info: FolderPathInfo) => {
        event.stopPropagation();
        navigateToFolder(info);
        setAnchorEl(null);
    }, [navigateToFolder]);

    const isSingleFolder = folderPaths.length === 1;
    const open = Boolean(anchorEl);

    if (_.isEmpty(folderPaths)) {
    // Grid view: render nothing to avoid gap
        if (isGrid) {
            return null;
        }

        // Not in any folder: show "--" in list view
        return (
            <NoFolderPlaceholder>
                <span>--</span>
            </NoFolderPlaceholder>
        );
    }

    const pathLabel = isSingleFolder ? (
        <TruncatedTooltip
            title={<LibraryBreadcrumb pathParts={folderPaths[0].pathParts} />}
            tooltipClasses={{ tooltip: FOLDER_TOOLTIP_CLASS }}
        >
            <SinglePathLabel className="folder-path-text">{folderPaths[0].displayLabel}</SinglePathLabel>
        </TruncatedTooltip>
    ) : (
        <span>{folderPaths.length}</span>
    );

    return (
        <>
            <FolderTooltipStyles />
            {showDivider && isGrid && <Divider vertical margin={8} style={{ background: "#DDD", borderRadius: "100px" }} />}
            <FolderTrigger
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                    e.stopPropagation();
                    if (isSingleFolder) {
                        navigateToFolder(folderPaths[0]);
                    } else {
                        setAnchorEl(anchorEl ? null : e.currentTarget);
                    }
                }}
            >
                <Icon medium>folder</Icon>
                <PathLabelWrapper>{pathLabel}</PathLabelWrapper>
            </FolderTrigger>

            {!isSingleFolder && (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                    onClick={e => e.stopPropagation()}
                >
                    <PopoverPaper>
                        {folderPaths.map(info => (
                            <FolderPathItem
                                key={info.folderId}
                                onClick={e => handlePathClick(e, info)}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <Icon>folder</Icon>
                                <TruncatedTooltip
                                    title={<LibraryBreadcrumb pathParts={info.pathParts} />}
                                    tooltipClasses={{ tooltip: FOLDER_TOOLTIP_CLASS }}
                                >
                                    <FolderPathItemLabel className="folder-path-item-label">{info.displayLabel}</FolderPathItemLabel>
                                </TruncatedTooltip>
                            </FolderPathItem>
                        ))}
                    </PopoverPaper>
                </Popover>
            )}
        </>
    );
}
